// GoLivePanel — May 29, 2026.
//
// Super Admin → guided flow to flip from Stripe sandbox to live mode.
// Steps:
//   1. Verify your 3 banks are attached in Stripe Dashboard (we can't
//      attach them via API for the platform account — Dashboard only).
//   2. Paste each bucket's ba_xxx ID + click "Validate" per row.
//   3. (Optional) Paste live API keys + click "Validate live keys" to see
//      the live account info BEFORE committing.
//   4. Type "GO-LIVE-CONFIRMED" + click "Promote to live" to atomically
//      swap. Rollback button below in case anything looks wrong.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle, Warning, Rocket, ArrowCounterClockwise, Spinner, ArrowSquareOut,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { usePublicPricing, fmt$ } from "@/lib/usePublicPricing";
import GoLiveRunbook from "@/components/GoLiveRunbook";
import LaunchDayCheck from "@/components/LaunchDayCheck";

const BUCKET_LABELS = {
  reservoir: "Reservoir (LLM consoles ACH-pull from here)",
  site_profits: "Site Profits (your business profits)",
  founder_personal: "Founder Personal (your creator earnings)",
};

export default function GoLivePanel() {
  const pricing = usePublicPricing();  // Feb 2026 (Randy pt-batch5c-dynamic-admin) — live prices in the webhook copy.
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [validations, setValidations] = useState({}); // ba_id → result
  const [baIds, setBaIds] = useState({ reservoir: "", site_profits: "", founder_personal: "" });
  const [liveKeys, setLiveKeys] = useState({ secret: "", publishable: "", webhook: "" });
  const [liveAcct, setLiveAcct] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [showPromote, setShowPromote] = useState(false);
  const [imp, setImp] = useState(null); // impersonation kill-switch status
  // Sep 1, 2026 (Randy, Go-Live audit) — the backend has always had a blocking
  // webhook preflight and an email kill-switch check; neither was ever shown
  // here, so the first sign of trouble was a 400 at the moment of promotion.
  const [preflight, setPreflight] = useState(null);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [email, setEmail] = useState(null);

  const runPreflight = async (secret) => {
    setPreflightBusy(true);
    try {
      const r = await api.post(
        "/admin/reservoir/go-live/webhook-preflight",
        secret ? { stripe_secret_key: secret } : {},
      );
      setPreflight(r.data);
      return r.data;
    } catch (e) {
      setPreflight({
        error: e.response?.data?.detail || "Webhook preflight failed.",
        blocks_go_live: true, endpoints: [], missing_events: [],
      });
      return null;
    } finally { setPreflightBusy(false); }
  };

  const reload = async () => {
    try {
      const r = await api.get("/admin/reservoir/go-live/status");
      setStatus(r.data);
      // Pre-fill from existing buckets
      const fromBuckets = {};
      for (const b of r.data.buckets || []) {
        if (b.ba_id) fromBuckets[b.key] = b.ba_id;
      }
      setBaIds((prev) => ({ ...prev, ...fromBuckets }));
      try {
        const ir = await api.get("/admin/impersonation/status");
        setImp(ir.data);
      } catch { /* non-fatal */ }
      try {
        const er = await api.get("/admin/email-quota");
        setEmail(er.data);
      } catch { /* non-fatal */ }
      runPreflight(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load go-live status.");
    }
  };

  const disableImpersonation = async () => {
    setBusy(true);
    try {
      await api.post("/admin/impersonation/toggle", { enabled: false });
      toast.success("Impersonation turned off. You're clear to go live.");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update impersonation.");
    } finally { setBusy(false); }
  };

  useEffect(() => { reload(); }, []);

  const validateBank = async (bucketKey) => {
    const baId = baIds[bucketKey];
    if (!baId) return toast.error("Paste a ba_xxx first.");
    setBusy(true);
    try {
      const r = await api.post("/admin/reservoir/validate-bank", { ba_id: baId });
      setValidations((v) => ({ ...v, [bucketKey]: r.data }));
      if (r.data.ok) toast.success(`${bucketKey}: ${r.data.verdict}`);
      else toast.error(`${bucketKey}: ${r.data.message}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation failed.");
    } finally { setBusy(false); }
  };

  const saveBaIdToBucket = async (bucketKey) => {
    const baId = baIds[bucketKey];
    if (!baId) return toast.error("Paste a ba_xxx first.");
    setBusy(true);
    try {
      await api.put("/admin/reservoir/bank-routing", {
        bucket: bucketKey,
        ba_id: baId,
      });
      toast.success(`Saved ${bucketKey} → ${baId.substring(0, 12)}...`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const validateLiveKeys = async () => {
    if (!liveKeys.secret) return toast.error("Paste the sk_live_ secret key first.");
    setBusy(true);
    try {
      const r = await api.post("/admin/reservoir/go-live/validate", {
        stripe_live_secret_key: liveKeys.secret,
        stripe_live_publishable_key: liveKeys.publishable || null,
        stripe_live_webhook_secret: liveKeys.webhook || null,
      });
      setLiveAcct(r.data);
      toast.success(`Valid live key — account ${r.data.stripe_account_id}.`);
      // Inspect the LIVE account's webhook endpoints with the same key, so the
      // blocking condition is visible before Step 4 rather than inside its error.
      await runPreflight(liveKeys.secret);
    } catch (e) {
      setLiveAcct(null);
      toast.error(e.response?.data?.detail || "Live key validation failed.");
    } finally { setBusy(false); }
  };

  const promote = async () => {
    if (confirm !== "GO-LIVE-CONFIRMED") {
      return toast.error("Type GO-LIVE-CONFIRMED exactly.");
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/reservoir/go-live/promote", {
        confirmation: "GO-LIVE-CONFIRMED",
        stripe_live_secret_key: liveKeys.secret,
        stripe_live_publishable_key: liveKeys.publishable || null,
        stripe_live_webhook_secret: liveKeys.webhook || null,
        ba_id_reservoir: baIds.reservoir || null,
        ba_id_site_profits: baIds.site_profits || null,
        ba_id_founder_personal: baIds.founder_personal || null,
      });
      toast.success(`Promoted to LIVE. Rollback id: ${r.data.rollback_id.substring(0, 8)}...`);
      setShowPromote(false);
      setConfirm("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Promote failed.");
    } finally { setBusy(false); }
  };

  const rollback = async () => {
    if (!confirm("Roll back to sandbox mode? Live keys will be cleared.")) return;
    setBusy(true);
    try {
      await api.post("/admin/reservoir/go-live/rollback");
      toast.success("Rolled back to sandbox mode.");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rollback failed.");
    } finally { setBusy(false); }
  };

  if (!status) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#6B6A68]" data-testid="golive-loading">
        <Spinner size={14} className="animate-spin" /> Loading Go-Live wizard…
      </div>
    );
  }

  const isLive = status.current_mode === "live";
  // Only a LIVE-key preflight can block promotion; a sandbox preflight is
  // informational (you are not promoting the test account).
  const webhookBlocks = !!(preflight && preflight.blocks_go_live && preflight.mode === "live");

  return (
    <section className="mt-10 pt-8 border-t hairline space-y-6" data-testid="golive-panel">
      <div className="flex items-baseline gap-3">
        <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Go-Live wizard</h2>
        <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded ${isLive ? 'bg-[#2F6B3A] text-white' : 'bg-[#6B6A68] text-white'}`}>
          {status.current_mode}
        </span>
      </div>
      <p className="text-xs text-[#6B6A68] leading-relaxed">
        When you're ready to flip from sandbox to real money, do this once.
        Stripe locks platform-account bank management to Dashboard, so step 1 is
        manual. Everything else is automated.
      </p>

      {/* Stripe Dashboard link + post-nuke sandbox instructions */}
      <div className="bg-white border hairline rounded-lg p-4 space-y-3" data-testid="golive-stripe-dashboard">
        <a
          href="https://dashboard.stripe.com/test/developers"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="golive-stripe-dashboard-link"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#635BFF] hover:text-[#4B45CC] transition-colors"
        >
          <ArrowSquareOut size={16} weight="bold" />
          Open Stripe Dashboard
        </a>
        <div className="rounded-md bg-[#FDF8F0] border border-[#E8D9BE] p-3" data-testid="golive-postnuke-instructions">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#9A7B3F] mb-1.5">
            After a sandbox nuke — do this in Stripe
          </div>
          <p className="text-[12px] text-[#6B6A68] leading-relaxed">
            The nuke wipes <strong>this app's database</strong>, but it can't reset
            Stripe's test balance — that money lives in Stripe, not here. So after
            every sandbox nuke, finish the reset on Stripe's side:
          </p>
          <ol className="list-decimal list-inside mt-2 space-y-1 text-[12px] text-[#6B6A68]">
            <li>Open the Stripe Dashboard (link above) and confirm you're in <strong>Test mode</strong> (toggle, top-right).</li>
            <li>Go to <strong>Developers → Overview</strong> (or your account menu) and click <strong>“Delete all test data.”</strong></li>
            <li>Confirm. This clears every test charge, customer, subscription &amp; the leftover <strong>pending balance</strong> so your Platform Usage Reservoir returns to <strong>$0</strong>.</li>
            <li>Come back here and refresh — your numbers now start from a true ground zero.</li>
          </ol>
          <p className="text-[11px] text-[#9A7B3F] mt-2 italic">
            Tip: run the nuke with “refund Stripe sandbox” ON first, then do the Dashboard delete for a guaranteed clean slate.
          </p>
        </div>
        <div className="rounded-md bg-[#EEF2FF] border border-[#C7D2FE] p-3" data-testid="golive-webhook-instructions">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#4B45CC] mb-1.5">
            Before going live — register the Stripe webhook
          </div>
          <p className="text-[12px] text-[#6B6A68] leading-relaxed">
            Webhooks are how Stripe tells the platform when payments succeed
            (subscriptions, the {fmt$(pricing.day_one_bundle_discount_threshold_cents)} gate, rev-share on every invoice). They're
            the production source of truth. Set this up once:
          </p>
          <ol className="list-decimal list-inside mt-2 space-y-1 text-[12px] text-[#6B6A68]">
            <li>Stripe Dashboard → <strong>Workbench → Webhooks → Event destinations</strong>. A test-mode destination does NOT carry over — live mode needs its own, with its own signing secret.</li>
            <li>Destination URL: <code className="bg-white px-1 rounded border">https://&lt;your-production-domain&gt;/api/webhooks/stripe</code></li>
            <li>
              You need <strong>TWO destinations at that same URL</strong>, because a destination has
              one scope and the platform needs both. A destination's scope is set when you create it
              and cannot be changed afterwards.
            </li>
            <li>
              Destination one — <strong>Events from: Your account</strong>. Tick these nine:{" "}
              <strong>checkout.session.completed</strong>,{" "}
              <strong>checkout.session.async_payment_succeeded</strong>,{" "}
              <strong>checkout.session.async_payment_failed</strong>,{" "}
              <strong>invoice.payment_succeeded</strong>, <strong>invoice.payment_failed</strong>,{" "}
              <strong>customer.subscription.deleted</strong>, <strong>charge.refunded</strong>,{" "}
              <strong>charge.dispute.created</strong>, <strong>charge.dispute.closed</strong>.
              Every charge here is a destination charge owned by the <em>platform</em> account, so
              these only ever arrive at a Your-account destination — no matter that a creator
              receives the funds. If the two <code className="bg-white px-1 rounded border">async_payment_*</code>{" "}
              events are missing from the picker, you are editing the wrong destination: Stripe
              filters the event catalogue by scope.
            </li>
            <li>
              Destination two — <strong>Events from: Connected accounts</strong>, carrying{" "}
              <code className="bg-white px-1 rounded border">account.updated</code> and nothing else.
              It flips each creator's charges/payouts-enabled flags and releases any voucher sweep
              stashed while their Connect onboarding was incomplete. Money events ticked here are
              dead weight — they will never be delivered.
            </li>
            <li>Copy the <strong>Your account</strong> destination's signing secret into <code className="bg-white px-1 rounded border">STRIPE_WEBHOOK_SECRET</code> and switch keys to live mode. The app holds one secret, and it has to be that one — it is the one carrying money.</li>
          </ol>
          <p className="text-[11px] text-[#4B45CC] mt-2 italic">
            The three charge events are the voucher money-movement events. Without them a refund
            never returns the credit, and a card dispute never freezes it — the credit
            stays spendable while the bank pulls the money back.
          </p>
          <p className="text-[11px] text-[#4B45CC] mt-2 italic">
            Because the app holds a single signing secret, the second destination's deliveries are
            rejected as bad signatures and <code className="bg-white px-1 rounded border">account.updated</code>{" "}
            never lands. Accepted trade-off: payout status still refreshes whenever a creator opens
            their Stripe screen, so it is slow rather than broken.
          </p>
          <p className="text-[11px] text-[#4B45CC] mt-2 italic">
            Do not treat webhooks as optional. Without the endpoint, a top-up only fulfils if the
            buyer actually lands back on the success page (close the tab and the money is taken with
            nothing credited), recurring rev-share never runs, and refunds and chargebacks are
            invisible to the platform entirely.
          </p>

          {/* Live webhook readiness — the blocking check, surfaced instead of hidden */}
          <div className="mt-3 rounded-md bg-white border hairline p-3" data-testid="golive-webhook-readiness">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#6B6A68]">
                Webhook readiness{preflight?.mode ? ` · ${preflight.mode} mode` : ""}
              </div>
              <button
                onClick={() => runPreflight(liveKeys.secret || null)}
                disabled={preflightBusy}
                data-testid="golive-webhook-recheck"
                className="text-[10px] px-2 py-1 rounded border hairline hover:border-[#4A5D4E] disabled:opacity-50"
              >{preflightBusy ? "Checking…" : "Re-check"}</button>
            </div>
            {!preflight ? (
              <div className="text-[11px] text-[#6B6A68]">Checking the endpoints on the active Stripe key…</div>
            ) : (
              <div className="space-y-1.5">
                <div className={`text-[12px] flex items-start gap-1.5 ${preflight.ok ? 'text-[#2F6B3A]' : 'text-[#7A3A2A]'}`}>
                  {preflight.ok
                    ? <CheckCircle size={13} weight="duotone" className="mt-0.5 shrink-0" />
                    : <Warning size={13} weight="duotone" className="mt-0.5 shrink-0" />}
                  <span data-testid="golive-webhook-verdict">
                    {preflight.ok
                      ? "Every event the handler implements is subscribed, at a scope that can deliver it."
                      : (preflight.error
                        || ((preflight.missing_events || []).length > 0
                          && `Missing: ${preflight.missing_events.join(", ")}`)
                        || (preflight.scope_blockers || [])[0]
                        || "The webhook configuration needs attention.")}
                  </span>
                </div>
                {!preflight.ok && (preflight.missing_voucher_events || []).length > 0 && (
                  <div className="text-[11px] text-[#7A3A2A]">
                    Voucher events missing: <strong>{preflight.missing_voucher_events.join(", ")}</strong> — refunds
                    and disputes would never reach the platform.
                  </div>
                )}
                {(preflight.scope_blockers || []).map((s) => (
                  <div key={s} className="text-[11px] text-[#7A3A2A]"
                    data-testid="golive-webhook-scope-blocker">{s}</div>
                ))}
                {(preflight.scope_warnings || []).map((s) => (
                  <div key={s} className="text-[11px] text-[#8A6320]"
                    data-testid="golive-webhook-scope-warning">{s}</div>
                ))}
                {(preflight.endpoints || []).length > 0 && (
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A68] pt-1"
                    data-testid="golive-endpoint-list-informational">
                    Endpoint list, scopes &amp; API versions — informational, does not block promote
                  </div>
                )}
                {(preflight.endpoints || []).map((ep) => (
                  <div key={ep.id} className="text-[11px] text-[#6B6A68] font-mono"
                    data-testid={`golive-endpoint-row-${ep.id}`}>
                    <div className="flex flex-wrap gap-x-2">
                      <span className={ep.has_required_events ? "text-[#2F6B3A]" : "text-[#7A3A2A]"}>
                        {ep.has_required_events ? "✓" : "✕"}
                      </span>
                      <span>{ep.url}</span>
                      <span>· {ep.status}</span>
                      <span>· {ep.event_count} events</span>
                      <span>· {ep.livemode ? "LIVE" : "test"}</span>
                      {ep.api_version && <span>· v{ep.api_version}</span>}
                      <span className={ep.scope_known ? "" : "italic"}>· from: {ep.scope || "unknown"}</span>
                    </div>
                    {(ep.dead_subscriptions || []).length > 0 && (
                      <div className="pl-4 text-[10px] text-[#8A6320]"
                        data-testid={`golive-endpoint-dead-subs-${ep.id}`}>
                        never delivered at this scope: {ep.dead_subscriptions.join(", ")}
                      </div>
                    )}
                  </div>
                ))}
                {(preflight.api_version_notes || []).map((n) => (
                  <div key={n} className="text-[11px] text-[#6B6A68]" data-testid="golive-webhook-api-version-note">{n}</div>
                ))}
                {preflight.signing_secret_configured === false && (
                  <div className="text-[11px] text-[#7A3A2A]">
                    No signing secret is configured. Webhook processing fails closed — every event is
                    rejected until <code>STRIPE_WEBHOOK_SECRET</code> is set.
                  </div>
                )}
                <div className="text-[10px] text-[#6B6A68] italic" data-testid="golive-webhook-scope-note">
                  {preflight.scope_verified
                    ? "Each destination's scope was read from Stripe, so “your account” vs “connected accounts” above is verified — not left to the eye."
                    : "Stripe's event-destination scope could not be read on this key, so “your account” vs “connected accounts” is unverified here. Check it in the Dashboard."}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Step 0 — privacy gate: impersonation must be OFF before going live */}
      {imp && (
        <div className={`rounded-lg p-4 border-2 ${imp.impersonation_enabled ? 'border-[#C16E5A] bg-[#FDF3F0]' : 'border-[#9BB99F] bg-[#F0F7F1]'}`} data-testid="golive-impersonation-gate">
          <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-2">Step 0 · Privacy gate</div>
          {imp.impersonation_enabled ? (
            <div className="space-y-2">
              <div className="text-sm text-[#7A3A2A] flex items-start gap-1.5">
                <Warning size={15} weight="duotone" className="mt-0.5 shrink-0" />
                <span>The impersonation tool is still ON. You promised users that nobody — not even you — can read their private data. Turn it off before going live.</span>
              </div>
              <button onClick={disableImpersonation} disabled={busy}
                data-testid="golive-disable-impersonation"
                className="text-xs px-3 py-2 rounded bg-[#C16E5A] text-white hover:bg-[#A35947] disabled:opacity-50">
                Turn off impersonation
              </button>
            </div>
          ) : (
            <div className="text-sm text-[#2F6B3A] flex items-center gap-1.5">
              <CheckCircle size={15} weight="duotone" />
              Impersonation is OFF. Your privacy promise holds — you're clear to go live.
            </div>
          )}
        </div>
      )}

      {/* Step 1 */}
      <div className="bg-white border hairline rounded-lg p-4">
        <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-2">Step 1 · Attach your bank(s) in Stripe Dashboard</div>
        <div className="text-[11px] text-[#6B6A68] leading-relaxed">
          Open Stripe Dashboard → Settings → Payouts → External accounts → Add bank.
          For sandbox testing, use routing <code className="bg-[#FDFCF8] px-1 rounded">110000000</code> + accounts:
          <ul className="list-disc list-inside mt-1 ml-2 font-mono">
            <li><code>000123456789</code></li>
            <li><code>000111111111</code></li>
            <li><code>000222222222</code></li>
          </ul>
          For live mode, use the real routing+account from the Relay account(s) you want to use.
          After adding each bank, Stripe shows a <code className="bg-[#FDFCF8] px-1 rounded">ba_xxx</code> ID in the bank's details. Copy it.
          <p className="mt-2 text-[#1C1B1A]">
            <strong>Only one bucket is wired to anything: Site Profits.</strong> It is the
            destination the profit sweep pays to. <em>Reservoir</em> and <em>Founder Payouts</em> are
            labels for your own bookkeeping — no code reads them, and your own creator earnings
            arrive through your Stripe Connect Express account, which pays out to the bank you
            attached inside Express onboarding (a different Stripe object entirely). Attach one bank
            if that's all you want; attach three if you prefer the separation.
          </p>
          <p className="mt-2 text-[#9A7B3F]">
            <strong>Where money actually lands day to day:</strong> Stripe's own payout schedule
            decides, not these buckets. On the default <strong>automatic</strong> schedule Stripe
            sweeps the whole available balance to whichever external account is marked{" "}
            <strong>DEFAULT</strong> in Stripe — the profit sweep below then usually finds $0 to
            move. Set the schedule to <strong>manual</strong> (Stripe → Settings → Payouts) if you
            want the sweep to be the thing that moves profit. Either choice is fine; just know which
            one you're on.
          </p>
          <p className="mt-2 text-[#7A3A2A]">
            Then finish each bucket you use in <strong>Financials → Platform Usage Reservoir</strong> by
            filling its nickname and last 4 digits. The{" "}
            <code className="bg-[#FDFCF8] px-1 rounded">ba_xxx</code> alone is not enough: a profit
            sweep refuses to run until Site Profits has an account last-4 on file.
          </p>
        </div>
      </div>

      {/* Step 2 */}
      <div className="bg-white border hairline rounded-lg p-4 space-y-3">
        <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68]">Step 2 · Paste each bank's ba_xxx ID below</div>
        <p className="text-[11px] text-[#7A3A2A]">
          “Validate” asks Stripe for a 1¢ payout to that bank to prove it exists. In sandbox there is
          no balance, so nothing moves. <strong>In live mode with an available balance a real 1¢
          payout can actually be sent.</strong>
        </p>
        {["reservoir", "site_profits", "founder_personal"].map((b) => {
          const v = validations[b];
          return (
            <div key={b} className="border hairline rounded p-3" data-testid={`golive-bucket-${b}`}>
              <div className="text-xs text-[#1C1B1A] mb-2">{BUCKET_LABELS[b]}</div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="ba_xxx..."
                  value={baIds[b]}
                  onChange={(e) => setBaIds((p) => ({ ...p, [b]: e.target.value }))}
                  data-testid={`golive-ba-input-${b}`}
                  className="flex-1 px-2 py-1.5 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#4A5D4E] font-mono"
                />
                <button
                  onClick={() => validateBank(b)}
                  disabled={busy || !baIds[b]}
                  data-testid={`golive-validate-${b}`}
                  className="text-[11px] px-2.5 py-1.5 rounded border hairline hover:border-[#4A5D4E] disabled:opacity-50"
                >Validate</button>
                <button
                  onClick={() => saveBaIdToBucket(b)}
                  disabled={busy || !baIds[b]}
                  data-testid={`golive-save-${b}`}
                  className="text-[11px] px-2.5 py-1.5 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50"
                >Save</button>
              </div>
              {v && (
                <div className={`text-[11px] mt-2 flex items-start gap-1.5 ${v.ok ? 'text-[#2F6B3A]' : 'text-[#7A3A2A]'}`}>
                  {v.ok ? <CheckCircle size={11} weight="duotone" className="mt-0.5" /> : <Warning size={11} weight="duotone" className="mt-0.5" />}
                  <span>{v.verdict}: {v.message || 'OK'}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Step 3 — Live key validation (skip in sandbox) */}
      <div className="bg-white border hairline rounded-lg p-4 space-y-3">
        <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68]">Step 3 · Live keys (skip while testing in sandbox)</div>
        <div className="text-[11px] text-[#6B6A68]">
          Only fill these in when you actually want to flip to live money. Paste from Stripe Dashboard → Developers → API keys (live mode toggle ON).
          The signing secret is <strong>not optional</strong> — promotion is refused without one,
          because webhook processing fails closed and Stripe would collect money that never fulfils.
        </div>
        <div className="grid grid-cols-1 gap-2">
          <input
            type="password" placeholder="sk_live_..." value={liveKeys.secret}
            onChange={(e) => setLiveKeys((p) => ({ ...p, secret: e.target.value }))}
            data-testid="golive-live-secret"
            className="px-2 py-1.5 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#4A5D4E] font-mono"
          />
          <input
            type="text" placeholder="pk_live_... (optional)" value={liveKeys.publishable}
            onChange={(e) => setLiveKeys((p) => ({ ...p, publishable: e.target.value }))}
            data-testid="golive-live-publishable"
            className="px-2 py-1.5 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#4A5D4E] font-mono"
          />
          <input
            type="password" placeholder="whsec_... (required before you can promote)" value={liveKeys.webhook}
            onChange={(e) => setLiveKeys((p) => ({ ...p, webhook: e.target.value }))}
            data-testid="golive-live-webhook"
            className="px-2 py-1.5 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#4A5D4E] font-mono"
          />
          <button
            onClick={validateLiveKeys}
            disabled={busy || !liveKeys.secret}
            data-testid="golive-validate-keys"
            className="self-start text-[11px] px-3 py-1.5 rounded border hairline hover:border-[#4A5D4E] disabled:opacity-50"
          >Validate live keys (read-only check)</button>
          {liveAcct && (
            <div className="bg-[#F0F7F1] border border-[#9BB99F] rounded p-3 text-[11px] text-[#1C1B1A] space-y-0.5">
              <div className="flex items-center gap-1 mb-1 text-[#2F6B3A]"><CheckCircle size={12} weight="duotone" /> <strong>Live key valid</strong></div>
              <div>Account: <code>{liveAcct.stripe_account_id}</code></div>
              <div>Business: {liveAcct.business_name || '—'}</div>
              <div>Country: {liveAcct.country} | currency: {liveAcct.default_currency}</div>
              <div>Charges enabled: {String(liveAcct.charges_enabled)} | Payouts enabled: {String(liveAcct.payouts_enabled)}</div>
              <div>Live balance: ${liveAcct.live_balance_cents / 100} avail · ${liveAcct.live_pending_cents / 100} pending</div>
            </div>
          )}
          {liveAcct?.webhook && (
            <div className={`rounded p-3 text-[11px] border ${liveAcct.webhook.blocking ? 'bg-[#FDF3F0] border-[#C16E5A] text-[#7A3A2A]' : 'bg-[#F0F7F1] border-[#9BB99F] text-[#2F6B3A]'}`}
              data-testid="golive-live-webhook-verdict">
              {liveAcct.webhook.message}
            </div>
          )}
        </div>
      </div>

      {/* Step 3b — email delivery. Every refund and dispute notice rides on this,
          and send_email_async fails SILENTLY, which is what makes it dangerous. */}
      <div className="bg-white border hairline rounded-lg p-4 space-y-2" data-testid="golive-email-readiness">
        <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] flex items-center justify-between gap-2 flex-wrap">
          <span>Step 3b · Email delivery (refund &amp; dispute notices)</span>
          <span className="text-[10px] normal-case tracking-normal px-1.5 py-0.5 rounded bg-[#F3F2EE] text-[#6B6A68] border hairline"
            data-testid="golive-email-informational-badge">
            Informational — does not block promote
          </span>
        </div>
        {!email ? (
          <div className="text-[11px] text-[#6B6A68]">Checking email configuration…</div>
        ) : (
          <div className="space-y-1">
            {[
              ["Resend API key", email.resend_api_key_configured, "RESEND_API_KEY is set", "RESEND_API_KEY is missing — every notice is dropped silently"],
              ["Outbound sends", !email.email_disabled, "Enabled", "EMAIL_DISABLED=1 — the preview kill switch is ON. Unset it in production."],
              ["Sender domain", email.sender_domain_verified, `Verified (${email.sender})`, `Unverified sender ${email.sender} — Resend only delivers to your own verified addresses`],
              ["Reply-to", email.reply_to_configured, "RESEND_REPLY_TO is set", "No reply-to — customer replies to a refund notice go nowhere"],
              ["Recipient allowlist", !(email.allowlist || []).length, "None — mail reaches whoever it is addressed to", `EMAIL_ALLOWLIST=${(email.allowlist || []).join(', ')} — every other recipient is withheld. This is the preview-only sandbox; the variable is inert off a preview host.`],
              ["Admin alerts", !/@send\./.test(email.admin_alert_to || ''), `Dispute + report alerts go to ${email.admin_alert_to}`, `Alerts go to ${email.admin_alert_to} — a send-only subdomain with no mailbox. Set RESEND_TO to a real inbox.`],
            ].map(([label, ok, good, bad]) => (
              <div key={label} className={`text-[11px] flex items-start gap-1.5 ${ok ? 'text-[#2F6B3A]' : 'text-[#7A3A2A]'}`}>
                {ok ? <CheckCircle size={12} weight="duotone" className="mt-0.5 shrink-0" /> : <Warning size={12} weight="duotone" className="mt-0.5 shrink-0" />}
                <span><strong className="text-[#1C1B1A]">{label}:</strong> {ok ? good : bad}</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-[#6B6A68] italic">
          Five notices depend on this: refund receipt, credit held (a dispute was opened), credit
          released (dispute won or resolved), credit removed (dispute lost), and purchase
          restriction. They are sent from the webhook handlers and never retried, so a missing key
          means the customer is never told their money came back.
        </p>
      </div>

      {/* Step 4 — Promote */}
      <div className="bg-[#FDFCF8] border-2 border-[#C16E5A] rounded-lg p-4">
        <div className="text-xs uppercase tracking-[0.22em] text-[#7A3A2A] mb-2 flex items-center gap-1.5">
          <Rocket size={12} weight="duotone" /> Step 4 · Promote to live (irreversible without rollback)
        </div>
        {!showPromote ? (
          <button
            onClick={() => setShowPromote(true)}
            data-testid="golive-promote-init"
            disabled={isLive || imp?.impersonation_enabled || webhookBlocks}
            className="text-xs px-3 py-2 rounded bg-[#C16E5A] text-white hover:bg-[#A35947] disabled:opacity-50"
          >
            {isLive ? "Already in LIVE mode"
              : imp?.impersonation_enabled ? "Turn off impersonation first (Step 0)"
              : webhookBlocks ? "Live webhook endpoint isn't ready (see readiness above)"
              : "I'm ready — show confirmation"}
          </button>
        ) : (
          <div className="space-y-2">
            <input
              type="text" placeholder="Type GO-LIVE-CONFIRMED exactly"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="golive-confirm-input"
              className="w-full px-2 py-1.5 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#C16E5A] font-mono"
            />
            <div className="flex gap-2">
              <button
                onClick={promote}
                disabled={busy || confirm !== "GO-LIVE-CONFIRMED" || imp?.impersonation_enabled || webhookBlocks}
                data-testid="golive-promote-fire"
                className="text-xs px-3 py-2 rounded bg-[#C16E5A] text-white hover:bg-[#A35947] disabled:opacity-50 flex items-center gap-1"
              ><Rocket size={11} weight="fill" /> Promote to LIVE</button>
              <button
                onClick={() => { setShowPromote(false); setConfirm(""); }}
                data-testid="golive-promote-cancel"
                className="text-xs px-3 py-2 rounded border hairline hover:border-[#6B6A68]"
              >Cancel</button>
            </div>
          </div>
        )}
        {isLive && (
          <button
            onClick={rollback}
            disabled={busy}
            data-testid="golive-rollback-btn"
            className="mt-3 text-[11px] px-3 py-1.5 rounded border border-[#6B6A68] hover:border-[#7A3A2A] flex items-center gap-1.5"
          ><ArrowCounterClockwise size={11} /> Roll back to sandbox</button>
        )}
      </div>

      {/* Step 5 — after the flip: did the money path actually work? */}
      <LaunchDayCheck />

      {/* Runbook — everything the wizard can't automate. Last, and collapsed. */}
      <GoLiveRunbook status={status} preflight={preflight} email={email}
        imp={imp} liveAcct={liveAcct} />
    </section>
  );
}
